import React, { useState, useEffect, useCallback } from 'react';

const STAGE_LABELS = {
  stage1: 'Stage 1: Marketing Plan',
  stage2: 'Stage 2: Campaign Plan',
  stage3a: 'Stage 3a: Structure',
  stage3b: 'Stage 3b: Explanations',
  stage4: 'Stage 4: Payload Conversion'
};

const formatNumber = (n) => (n || 0).toLocaleString();

const formatCost = (cost) => {
  if (cost == null || cost === 0) return '—';
  if (cost < 0.01) return '< $0.01';
  return `$${cost.toFixed(2)}`;
};

const ReportsList = ({ isOpen, onClose, isAdmin = false, companySlug }) => {
  // Helper: append company_slug to API URLs for per-tab company scoping
  const scopeUrl = (url) => {
    if (!companySlug) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}company_slug=${companySlug}`;
  };
  const [reports, setReports] = useState([]);
  const [queueData, setQueueData] = useState({ in_progress: [], pending: [] });
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reportsResult, queueResult] = await Promise.allSettled([
        fetch(scopeUrl('/api/meta_ads_ai/reports'), { headers: { 'Accept': 'application/json' } }),
        fetch(scopeUrl('/api/meta_ads_ai/reports/queue_status'), { headers: { 'Accept': 'application/json' } })
      ]);

      if (reportsResult.status === 'fulfilled' && reportsResult.value.ok) {
        const data = await reportsResult.value.json();
        setReports(data.reports || data || []);
      }
      if (queueResult.status === 'fulfilled' && queueResult.value.ok) {
        const data = await queueResult.value.json();
        setQueueData(data);
      }
    } catch (err) {
      console.error('Error fetching reports data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      setSelectedReport(null);
    }
  }, [isOpen, fetchData]);

  const fetchReportDetail = async (reportId) => {
    setDetailLoading(true);
    try {
      const response = await fetch(scopeUrl(`/api/meta_ads_ai/reports/${reportId}`), {
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedReport(data);
      }
    } catch (err) {
      console.error('Error fetching report detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { modifier: 'pending', label: 'Pending', icon: '\u25CB' },
      stage1_in_progress: { modifier: 'in-progress', label: 'Stage 1', icon: '\u25D0' },
      stage2_in_progress: { modifier: 'in-progress', label: 'Stage 2', icon: '\u25D1' },
      stage3_in_progress: { modifier: 'in-progress', label: 'Stage 3a', icon: '\u25D5' },
      stage3b_in_progress: { modifier: 'in-progress', label: 'Stage 3b', icon: '\u25D5' },
      stage4_in_progress: { modifier: 'in-progress', label: 'Stage 4', icon: '\u25D5' },
      completed: { modifier: 'completed', label: 'Completed', icon: '\u25CF' },
      failed: { modifier: 'failed', label: 'Failed', icon: '\u2715' }
    };
    return configs[status] || configs.pending;
  };

  const hasQueueItems = queueData.in_progress.length > 0 || queueData.pending.length > 0;

  if (!isOpen) return null;

  return (
    <div className="reports-list-modal-overlay" onClick={onClose}>
      <div
        className={`reports-list-modal-container ${selectedReport ? 'reports-list-modal-container--with-detail' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="reports-list-modal__header">
          <div className="reports-list-modal__title-row">
            {selectedReport && (
              <button
                type="button"
                className="reports-list-modal__back-btn"
                onClick={() => setSelectedReport(null)}
              >
                &larr;
              </button>
            )}
            <h2 className="reports-list-modal__title">
              {selectedReport ? 'Report Detail' : 'AI Generation Queue'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="reports-list-modal__close-btn">
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="reports-list-modal__content">
          {selectedReport ? (
            /* Detail View: Stage Token Table */
            detailLoading ? (
              <div className="reports-list-modal__loading">Loading report...</div>
            ) : (
              <div className="reports-detail">
                {/* Report header info */}
                <div className="reports-detail__header">
                  <div className="reports-detail__header-row">
                    <span className="reports-detail__label">Report ID:</span>
                    <span>{selectedReport.id}</span>
                  </div>
                  <div className="reports-detail__header-row">
                    <span className="reports-detail__label">Event:</span>
                    <span>{selectedReport.event_name || 'Unknown Event'}</span>
                  </div>
                  <div className="reports-detail__header-row">
                    <span className="reports-detail__label">Generated:</span>
                    <span>{selectedReport.created_at && new Date(selectedReport.created_at).toLocaleString()}</span>
                  </div>
                  {isAdmin && selectedReport.total_token_usage && (
                    <>
                      <div className="reports-detail__header-row">
                        <span className="reports-detail__label">Total Tokens<span className="admin-only-dot" />:</span>
                        <span>{formatNumber(selectedReport.total_token_usage.total)}</span>
                      </div>
                      <div className="reports-detail__header-row">
                        <span className="reports-detail__label">Est. Cost<span className="admin-only-dot" />:</span>
                        <span>{selectedReport.estimated_cost || '—'}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Stage token breakdown table */}
                {isAdmin && selectedReport.token_usage && Object.keys(selectedReport.token_usage).length > 0 ? (
                  <div className="reports-detail__table-wrap">
                    <table className="reports-detail__token-table">
                      <thead>
                        <tr>
                          <th>Stage</th>
                          <th>Input</th>
                          <th>Cached</th>
                          <th>Reasoning</th>
                          <th>Output</th>
                          <th>Total</th>
                          <th>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(selectedReport.token_usage).map(([stage, usage]) => (
                          <tr key={stage}>
                            <td>{STAGE_LABELS[stage] || stage}</td>
                            <td>{formatNumber(usage.input)}</td>
                            <td>{formatNumber(usage.cached)}</td>
                            <td>{formatNumber(usage.reasoning)}</td>
                            <td>{formatNumber(usage.output)}</td>
                            <td><strong>{formatNumber(usage.total)}</strong></td>
                            <td>{formatCost(usage.cost)}</td>
                          </tr>
                        ))}
                        {selectedReport.total_token_usage && (
                          <tr className="reports-detail__total-row">
                            <td><strong>Total</strong></td>
                            <td><strong>{formatNumber(selectedReport.total_token_usage.input)}</strong></td>
                            <td><strong>{formatNumber(selectedReport.total_token_usage.cached)}</strong></td>
                            <td><strong>{formatNumber(selectedReport.total_token_usage.reasoning)}</strong></td>
                            <td><strong>{formatNumber(selectedReport.total_token_usage.output)}</strong></td>
                            <td><strong>{formatNumber(selectedReport.total_token_usage.total)}</strong></td>
                            <td><strong>{formatCost(selectedReport.total_token_usage.cost)}</strong></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : !isAdmin ? (
                  <div className="reports-detail__non-admin">
                    <p>Token usage details are only available to administrators.</p>
                  </div>
                ) : (
                  <div className="reports-detail__non-admin">
                    <p>No token usage data available for this report.</p>
                  </div>
                )}
              </div>
            )
          ) : loading ? (
            <div className="reports-list-modal__loading">
              Loading...
            </div>
          ) : (
            <div className="reports-list-modal__list">
              {/* Live Queue Section */}
              {hasQueueItems && (
                <div className="reports-list-queue-section">
                  <h3 className="reports-list-queue-section__title">Active Queue</h3>
                  {queueData.in_progress.map(item => (
                    <div key={item.id} className="reports-list-item reports-list-item--queue">
                      <div className="reports-list-item__row">
                        <div>
                          <h4 className="reports-list-item__title">{item.event_name || 'Unknown Event'}</h4>
                          <p className="reports-list-item__date">{item.stage_label}</p>
                        </div>
                        <div className="reports-list-item__status-group">
                          {isAdmin && item.estimated_cost && (
                            <span className="reports-list-item__cost">
                              {item.estimated_cost}<span className="admin-only-dot" />
                            </span>
                          )}
                          <span className="reports-list-item__badge reports-list-item__badge--in-progress">
                            <span className="reports-list-item__spinner" />
                            In Progress
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {queueData.pending.map(item => (
                    <div key={item.id} className="reports-list-item reports-list-item--queue">
                      <div className="reports-list-item__row">
                        <div>
                          <h4 className="reports-list-item__title">{item.event_name || 'Unknown Event'}</h4>
                          <p className="reports-list-item__date">Waiting in queue</p>
                        </div>
                        <span className="reports-list-item__badge reports-list-item__badge--pending">
                          <span>{'\u25CB'}</span>
                          Queued
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Completed / Failed Reports */}
              {reports.length === 0 && !hasQueueItems ? (
                <div className="reports-list-modal__empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="reports-list-modal__empty-icon">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <p className="reports-list-modal__empty-title">No reports yet</p>
                  <p className="reports-list-modal__empty-sub">
                    Generate your first AI campaign to see reports here.
                  </p>
                </div>
              ) : (
                <>
                  {hasQueueItems && reports.length > 0 && (
                    <h3 className="reports-list-queue-section__title reports-list-queue-section__title--history">History</h3>
                  )}
                  {reports.map(report => {
                    const statusConfig = getStatusConfig(report.status);
                    return (
                      <div
                        key={report.id}
                        className="reports-list-item"
                        onClick={() => fetchReportDetail(report.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && fetchReportDetail(report.id)}
                      >
                        <div className="reports-list-item__row">
                          <div>
                            <h4 className="reports-list-item__title">
                              {report.event_name || 'Unknown Event'}
                            </h4>
                            <p className="reports-list-item__date">
                              {new Date(report.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="reports-list-item__status-group">
                            {isAdmin && report.estimated_cost && (
                              <span className="reports-list-item__cost">
                                {report.estimated_cost}<span className="admin-only-dot" />
                              </span>
                            )}
                            <span className={`reports-list-item__badge reports-list-item__badge--${statusConfig.modifier}`}>
                              <span>{statusConfig.icon}</span>
                              {statusConfig.label}
                            </span>
                          </div>
                        </div>
                        {report.error_message && (
                          <p className="reports-list-item__error" title={report.error_message}>
                            {report.error_message.length > 100
                              ? report.error_message.substring(0, 100) + '...'
                              : report.error_message}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsList;
